import React, { useState } from 'react';
import { formatApiError } from '../utils/formatApiError';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Trophy, Mail, Lock, User, Chrome, BadgeCheck } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { register } from '../services/api';

const Register = () => {
  const [username, setUsername] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [employeeNumber, setEmployeeNumber] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const validateEmployeeNumber = (value) => {
    if (!value) return false;
    const lower = value.toLowerCase().trim();
    
    // Must start with hc0 or ex0
    if (!lower.startsWith('hc0') && !lower.startsWith('ex0')) {
      return false;
    }
    
    // Get suffix after prefix
    const suffix = lower.substring(3);
    
    // Must be exactly 5 digits
    if (!/^\d{5}$/.test(suffix)) {
      return false;
    }
    
    // Must be between 00001 and 99999
    const num = parseInt(suffix, 10);
    if (num < 1 || num > 99999) {
      return false;
    }
    
    return true;
  };

  const getEmployeeNumberError = (value) => {
    if (!value) return null;
    const lower = value.toLowerCase().trim();
    
    if (!lower.startsWith('hc0') && !lower.startsWith('ex0')) {
      return 'Moet beginnen met "hc0" of "ex0"';
    }
    
    const suffix = lower.substring(3);
    
    if (!/^\d*$/.test(suffix)) {
      return 'Alleen cijfers toegestaan na "hc0" of "ex0"';
    }
    
    if (suffix.length !== 5) {
      return 'Exact 5 cijfers na "hc0" of "ex0" (bijv. hc012345)';
    }
    
    const num = parseInt(suffix, 10);
    if (num < 1) {
      return 'Nummer moet minimaal 00001 zijn';
    }
    
    return null;
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    if (!username || !firstName || !lastName || !email || !employeeNumber || !password || !confirmPassword) {
      toast({
        title: 'Fout',
        description: 'Vul alle velden in',
        variant: 'destructive',
      });
      return;
    }

    if (!validateEmployeeNumber(employeeNumber)) {
      toast({
        title: 'Fout',
        description: 'Personeelsnummer moet beginnen met "hc0" of "ex0"',
        variant: 'destructive',
      });
      return;
    }

    if (password !== confirmPassword) {
      toast({
        title: 'Fout',
        description: 'Wachtwoorden komen niet overeen',
        variant: 'destructive',
      });
      return;
    }

    if (password.length < 8) {
      toast({
        title: 'Fout',
        description: 'Wachtwoord moet minimaal 8 karakters bevatten',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      await register(username, email, password, employeeNumber, firstName, lastName);
      toast({
        title: 'Account aangemaakt!',
        description: `Welkom ${username}! Je kunt nu voorspellingen maken.`,
      });
      navigate('/');
    } catch (error) {
      toast({
        title: 'Fout',
        description: formatApiError(error, 'Registratie mislukt'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleRegister = () => {
    // Mock Google registration - will be implemented with backend
    toast({
      title: 'Google Registratie',
      description: 'Google registratie wordt binnenkort toegevoegd',
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1a2332] via-[#2d3e50] to-[#1a2332] flex items-center justify-center px-4 py-12">
      <Card className="w-full max-w-md shadow-2xl border-2 border-[#FDB913]">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="bg-[#FDB913] rounded-full p-4">
              <Trophy size={40} className="text-[#1a2332]" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-foreground">Registreren</CardTitle>
          <CardDescription>Maak een nieuw account aan om mee te doen</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleRegister} className="space-y-4">
            <div>
              <Label htmlFor="username" className="flex items-center space-x-2 mb-2">
                <User size={16} />
                <span>Gebruikersnaam</span>
              </Label>
              <Input
                id="username"
                data-testid="register-username"
                type="text"
                placeholder="Kies een gebruikersnaam"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="border-gray-300 focus:border-[#FDB913] focus:ring-[#FDB913]"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="firstName" className="flex items-center space-x-2 mb-2">
                  <User size={16} />
                  <span>Voornaam</span>
                </Label>
                <Input
                  id="firstName"
                  data-testid="register-first-name"
                  type="text"
                  placeholder="Voornaam"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="border-gray-300 focus:border-[#FDB913] focus:ring-[#FDB913]"
                />
              </div>
              <div>
                <Label htmlFor="lastName" className="flex items-center space-x-2 mb-2">
                  <User size={16} />
                  <span>Achternaam</span>
                </Label>
                <Input
                  id="lastName"
                  data-testid="register-last-name"
                  type="text"
                  placeholder="Achternaam"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="border-gray-300 focus:border-[#FDB913] focus:ring-[#FDB913]"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email" className="flex items-center space-x-2 mb-2">
                <Mail size={16} />
                <span>Email</span>
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="je@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="border-gray-300 focus:border-[#FDB913] focus:ring-[#FDB913]"
              />
            </div>

            <div>
              <Label htmlFor="employeeNumber" className="flex items-center space-x-2 mb-2">
                <BadgeCheck size={16} />
                <span>Personeelsnummer</span>
              </Label>
              <Input
                id="employeeNumber"
                type="text"
                placeholder="hc012345 of ex012345"
                value={employeeNumber}
                onChange={(e) => setEmployeeNumber(e.target.value)}
                className={`border-gray-300 focus:border-[#FDB913] focus:ring-[#FDB913] ${
                  employeeNumber && getEmployeeNumberError(employeeNumber) ? 'border-red-500' : ''
                }`}
              />
              {employeeNumber && getEmployeeNumberError(employeeNumber) && (
                <p className="text-red-500 text-sm mt-1">{getEmployeeNumberError(employeeNumber)}</p>
              )}
            </div>

            <div>
              <Label htmlFor="password" className="flex items-center space-x-2 mb-2">
                <Lock size={16} />
                <span>Wachtwoord</span>
              </Label>
              <Input
                id="password"
                type="password"
                placeholder="Minimaal 8 karakters"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="border-gray-300 focus:border-[#FDB913] focus:ring-[#FDB913]"
              />
            </div>

            <div>
              <Label htmlFor="confirmPassword" className="flex items-center space-x-2 mb-2">
                <Lock size={16} />
                <span>Bevestig Wachtwoord</span>
              </Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="Herhaal je wachtwoord"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="border-gray-300 focus:border-[#FDB913] focus:ring-[#FDB913]"
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-[#FDB913] text-[#1a2332] hover:bg-[#e5a911] font-bold text-lg py-6"
              disabled={loading}
            >
              {loading ? 'Bezig met registreren...' : 'Account Aanmaken'}
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-background text-muted-foreground">Of registreer met</span>
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            className="w-full border-border hover:bg-secondary"
            onClick={handleGoogleRegister}
          >
            <Chrome size={20} className="mr-2" />
            Google
          </Button>

          <div className="mt-6 text-center text-sm">
            <span className="text-muted-foreground">Al een account? </span>
            <Link to="/login" className="text-[#FDB913] font-semibold hover:underline">
              Log hier in
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Register;
